import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Demo credentials display - showing actual mock data from migration
  const demoCredentials = [
    { email: 'admin@jewelthread.com', password: 'JewelAdmin123', role: 'Admin' },
    { email: 'designer@jewelthread.com', password: 'Designer123', role: 'Designer' },
    { email: 'sales@jewelthread.com', password: 'Sales123', role: 'Sales Rep' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear errors when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData?.password?.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      await signIn(formData?.email, formData?.password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Sign in error:', error);
      setErrors({ 
        submit: error?.message || 'Invalid email or password. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fillDemoCredentials = (email, password) => {
    setFormData(prev => ({
      ...prev,
      email,
      password
    }));
    setErrors({});
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Input */}
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-foreground">
            Email
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData?.email}
            onChange={handleInputChange}
            className={`input-dark ${errors?.email ? 'border-destructive' : 'border-primary focus:border-primary focus:glow-teal'}`}
            placeholder="Enter your email address"
          />
          {errors?.email && (
            <p className="text-xs text-destructive">{errors?.email}</p>
          )}
        </div>

        {/* Password Input */}
        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium text-foreground">
            Password
          </label>
          <Input
            id="password"
            name="password"
            type="password"
            value={formData?.password}
            onChange={handleInputChange}
            className={`input-dark ${errors?.password ? 'border-destructive' : 'border-primary focus:border-primary focus:glow-teal'}`}
            placeholder="Enter your password"
          />
          {errors?.password && (
            <p className="text-xs text-destructive">{errors?.password}</p>
          )}
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="rememberMe"
              name="rememberMe"
              checked={formData?.rememberMe}
              onChange={handleInputChange}
            />
            <label htmlFor="rememberMe" className="text-sm text-foreground cursor-pointer">
              Remember Me
            </label>
          </div>
          <a
            href="#"
            className="text-sm text-primary hover:text-primary/80 transition-luxury"
            onClick={(e) => e?.preventDefault()}
          >
            Forgot Password?
          </a>
        </div>

        {/* Login Button - Teal */}
        <Button
          type="submit"
          className="w-full btn-teal transition-luxury-slow"
          disabled={isLoading}
        >
          {isLoading ? 'Signing In...' : 'Log In'}
        </Button>

        {/* Error Display */}
        {errors?.submit && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center">
            <p className="text-sm text-destructive">{errors?.submit}</p>
          </div>
        )}
      </form>
      {/* Demo Credentials Section */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Demo Credentials:</h4>
        <div className="space-y-2">
          {demoCredentials?.map((cred, index) => (
            <button
              key={index}
              onClick={() => fillDemoCredentials(cred?.email, cred?.password)}
              className="w-full text-left p-2 rounded border border-border hover:bg-muted/50 transition-luxury"
            >
              <div className="flex justify-between items-center">
                <span className="text-sm text-foreground">{cred?.role}</span>
                <span className="text-xs text-muted-foreground">Click to fill</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {cred?.email} / {cred?.password}
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Social Login Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">or</span>
        </div>
      </div>
      {/* Social Login Buttons - Dark Styled */}
      <div className="space-y-3">
        <Button
          type="button"
          className="w-full btn-dark-outline transition-luxury"
          onClick={(e) => e?.preventDefault()}
        >
          <svg className="mr-3 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Google
        </Button>
        
        <Button
          type="button"
          className="w-full btn-dark-outline transition-luxury"
          onClick={(e) => e?.preventDefault()}
        >
          <svg className="mr-3 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Facebook
        </Button>
      </div>
      {/* Sign Up Link */}
      <div className="text-center text-sm">
        <span className="text-muted-foreground">Don't have an account? </span>
        <a
          href="/signup"
          className="text-primary hover:text-primary/80 font-medium transition-luxury"
        >
          Sign Up
        </a>
      </div>
      {/* Terms - As per Figma design */}
      <div className="text-center text-xs text-muted-foreground leading-relaxed">
        By continuing, you agree to our{' '}
        <a href="#" className="text-primary hover:text-primary/80 transition-luxury">
          Terms of Service
        </a>
        {' '}and{' '}
        <a href="#" className="text-primary hover:text-primary/80 transition-luxury">
          Data Processing Agreement
        </a>
        .
      </div>
    </div>
  );
};

export default LoginForm;